"use client";

import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { deviceDetector, getItem, getTypeAppOrPc, setItem } from "utils";
import { useSnapshot } from "valtio/react";
import { useParams } from "next/navigation";
import { OspToast } from "@ospweb/sdk-ui";
import {
  AppRatingSuggestionContent,
  type AppRatingSuggestionContentMode,
} from "./AppRatingSuggestionContent";
import Translations from "@translate/components/Home/AppRating/AppRatingSuggestionModalContainer";
import { I18nUtil } from "@utils/i18n";
import EdgenDrawer from "@components/Drawer";
import { useBridge } from "@/src/lib/bridge";
import { useAppFeatures } from "@/src/lib/bridge/useBridge";
import { MessageType } from "@/src/lib/bridge/types";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { rootStore } from "@store/rootStore";
import { useCredits } from "@hooks/useCredits";
import { useGlobalAppModalLimit } from "@hooks/useGlobalAppModalLimit";
import { usePostTaskList } from "@/src/hooks/usePostTaskList";
import { ModalStore } from "@store/modules/modal";

const I18n = I18nUtil.createScoped(Translations);

const FIFTEEN_DAYS_MS = 15 * 24 * 60 * 60 * 1000;

const GLOBAL_APP_MODAL_KEY_APP_RATING = "app-rating";

/**
 * 是否满足「15 天内未展示过」可再次展示
 */
function canShowBy15Days(): boolean {
  const last = getItem(LOCAL_STORAGE_KEYS.APP_RATING_MODAL_LAST_SHOWN);
  if (last == null) {
    return true;
  }
  return Date.now() - Number(last) >= FIFTEEN_DAYS_MS;
}

/**
 * 记录本次展示时间
 */
function markShown(): void {
  setItem(LOCAL_STORAGE_KEYS.APP_RATING_MODAL_LAST_SHOWN, Date.now());
}

/**
 * App 内评分/建议弹窗 - 容器层
 * 仅 APP 环境渲染；使用 EdgenDrawer，管理 visible 与 contentMode；
 * 通过 pubsub.emit(pubSubKeyMap.OPEN_APP_RATING_SUGGESTION_MODAL) 打开弹窗。
 */
export const AppRatingSuggestionModalContainer: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [contentMode, setContentMode] =
    useState<AppRatingSuggestionContentMode>("rating");
  const [text, setText] = useState("");
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { memberUserData, status } = useCredits();
  const { locale } = useParams<{ locale: string }>();
  const { appRatingModalVisible } = useSnapshot(ModalStore);
  const { postTaskList: allTasks } = usePostTaskList({
    position: "list",
    channel: getTypeAppOrPc(),
    locale,
  });

  const { request } = useBridge();
  /**
   * KMP 原生已承载评分弹层（`features.nativeAppRating`）⇒ 本组件整个不参与，
   * 与 push 引导的 `nativePushPrimer` 同一手法，保证 App 内只有一份弹层。
   *
   * 不加这道判定的后果（2026-09-07 修复前的实际状态）：本组件挂在
   * `src/app/[locale]/layout.tsx` 的全局 layout 上、`isApp()` 为真就渲染，于是用户在 App 里
   * 打开任何 WebView 页（brief 卡详情 / crypto / report / 抽屉 www 页）10 秒后都会弹一次 web 版，
   * 与原生那份**各记各的冷却**（这里是 localStorage，原生是 LocalCache）⇒ 同一用户被弹两次。
   *
   * 老版本 App 不注入该字段 ⇒ undefined ⇒ 行为完全不变（旧壳里 web 这份仍是唯一实现）。
   */
  const nativeOwnsAppRating = useAppFeatures().nativeAppRating === true;

  const closeModalRef = useRef<() => void>(() => {});
  const { scheduleOpen, cancelPendingOpen } = useGlobalAppModalLimit(
    GLOBAL_APP_MODAL_KEY_APP_RATING,
    {
      hideInAppChatModal: true,
      onRemoteClaim: () => closeModalRef.current(),
    },
  );
  const cancelPendingOpenRef = useRef(cancelPendingOpen);
  cancelPendingOpenRef.current = cancelPendingOpen;

  const closeModal = useCallback(() => {
    cancelPendingOpenRef.current();
    setVisible(false);
    setContentMode("rating");
    ModalStore.appRatingModalVisible = false;
  }, []);
  closeModalRef.current = closeModal;

  const openModal = useCallback(() => {
    setContentMode("rating");
    const rated = getItem(LOCAL_STORAGE_KEYS.APP_RATING_MODAL_RATED);
    // 不是APP不显示 未登录不显示 已评分不显示 15天内展示过 不显示 原生已承载不显示
    if (
      !deviceDetector.isApp() ||
      nativeOwnsAppRating ||
      !isLoggedIn ||
      rated ||
      !canShowBy15Days()
    ) {
      return;
    }
    // EDG-479/481：web 版「开启通知」弹窗已删（App 内由原生 push 引导承担，
    // 且它在 App 里本就被 `features.nativePushPrimer` 挡住不渲染）⇒ 互斥项少一条。
    if (ModalStore.appVersionUpdateModalVisible) {
      return;
    }
    ModalStore.appRatingModalVisible = true;
    markShown();
    setVisible(true);
  }, [isLoggedIn, nativeOwnsAppRating]);

  useEffect(() => {
    if (!appRatingModalVisible && visible) {
      closeModal();
    }
  }, [appRatingModalVisible, visible, closeModal]);

  // 判断用户在会员有效期内
  const _isTodayPurchased = useMemo(() => {
    if (status !== "success") {
      return false;
    }
    return !memberUserData?.isFree;
  }, [memberUserData?.isFree, status]);

  // 判断是否消耗了200credit
  const _isConsumed200Credit = useMemo(() => {
    return (memberUserData?.credit?.used ?? 0) >= 200;
  }, [memberUserData?.credit?.used]);

  // 判断用户完成了所有的新手任务
  const _isCompletedAllNewbieTasks = useMemo(() => {
    const onboardingMissions = allTasks?.find(
      (task) => task.code === "onboarding_missions",
    );
    return (
      onboardingMissions?.status === 1 && onboardingMissions?.rewardStatus === 1
    );
  }, [allTasks]);

  useEffect(() => {
    // 原生承载时连 10 秒计时都不排（openModal 里也有一道，这里省掉无意义的定时器）。
    //
    // ⚠️ **必须先取消已排上的那次**，不能光 return：`useAppFeatures()` 初值是 `{}`，
    // `nativeAppRating` 要等 bridge 的 app-init 合并进来才为 true。若会员数据先到、
    // features 后到，10 秒计时**已经排上**，而 `scheduleOpen` 只认
    // `openAttemptGenerationRef`（`useGlobalAppModalLimit.ts:254`）—— 本 effect 重跑时
    // 直接 return 不会推进那个计数器，于是 10 秒后仍会执行**旧的** `openModal` 闭包
    //（那份闭包里 `nativeOwnsAppRating` 还是 false，守卫拦不住）。
    // 后果不只是白排一次：它会 `markShown()` 烧掉 15 天冷却、把
    // `ModalStore.appRatingModalVisible` 置 true 并广播 claim，而组件此刻渲染 `null`
    // ⇒ 没有任何路径能把它置回 false，`isPeerAutoModalOpen()`
    //（`hooks/reportOpening/useReportOpeningAutoshow.ts:38`）会**整个会话**压住开场页自动弹。
    if (nativeOwnsAppRating) {
      cancelPendingOpenRef.current();
      return;
    }
    if (
      _isTodayPurchased ||
      _isConsumed200Credit ||
      _isCompletedAllNewbieTasks
    ) {
      // 返回 cleanup（hook KDoc 的用法）：卸载 / 下一轮 attempt 时自动作废这次延迟打开
      return scheduleOpen(10000, openModal);
    }
  }, [
    scheduleOpen,
    openModal,
    _isTodayPurchased,
    _isConsumed200Credit,
    _isCompletedAllNewbieTasks,
    nativeOwnsAppRating,
  ]);

  const handleRateUs = useCallback(async () => {
    cancelPendingOpenRef.current();
    // 用户已进行评分
    await request(MessageType.REQUEST_APP_RATE);
    setItem(LOCAL_STORAGE_KEYS.APP_RATING_MODAL_RATED, true);
    closeModal();
  }, [request, closeModal]);

  const handleOfferSuggestion = useCallback(() => {
    setContentMode("suggestion");
  }, []);

  const handleSubmit = useCallback(() => {
    // 用户的吐槽通过日志上报到阿里云 阿里云定期进行导出到Google 表格
    closeModal();
    OspToast.success({
      content: I18n.t("Submission successful!"),
    });
  }, [closeModal, text]);

  if (!deviceDetector.isApp() || nativeOwnsAppRating) {
    return null;
  }

  return (
    <EdgenDrawer
      isOpen={visible}
      onClose={closeModal}
      placement="bottom"
      width="100%"
      contentClassName="pb-0 no-scrollbar"
      titleClassName="hidden"
      isMask={false}
      maskClosable={false}
      styles={{
        wrapper: {
          minHeight: "370px",
          height: "auto",
        },
        body: {
          padding: "24px 24px var(--safe-area-inset-bottom) 24px",
          scrollbarWidth: "none",
          minHeight: "370px",
        },
      }}
    >
      <AppRatingSuggestionContent
        mode={contentMode}
        onRateUs={handleRateUs}
        onOfferSuggestion={handleOfferSuggestion}
        onSubmit={handleSubmit}
        onMaybeLater={closeModal}
        text={text}
        setText={setText}
      />
    </EdgenDrawer>
  );
};
